/* ***************************************
This mixin "exposes" the computed properties...
  * localLetterAddingMode [ManualNikudPane (&KeyMap), NikudOptionsPane (=KeyMap), NikudResultsContainer, & NikudEdit]
  * localCurrentWordIndex [for ManualNikudPane, NikudOptionsPane, EditNikudPane, 
                          NikudResultsContainer, NikudEdit, and UserSettings]
  * localCurrentLetterIndex [for ManualNikudPane, NikudOptionsPane, EditNikudPane, 
                          NikudResultsContainer, NikudEdit, and UserSettings]
  * localCurrentWord [for ManualNikudPane (&KeyMap), NikudOptionsPane (&KeyMap), EditNikudPane, 
                          NikudResultsContainer, NikudEdit, and UserSettings]
  * currentLetterNotSep [ManualNikudPane (&KeyMap), NikudOptionsPane (&KeyMap), NikudResultsContainer, & NikudEdit]
... and the methods:
  * modalOpen() [manualNikudPaneKeyMap, nikudOptionsPaneKeyMap, NikudPaneContainer, NikudResultsContainer, & NikudEdit]
  * removeFromUserDictionary() [for NikudOptionsPane (&KeyMap) and ManualNikudPane]
  * getRelevantOptionsFromUserDictionary() [for NikudOptionsPane]
  * display() [for ManualNikudPane, EditNikudPane, NikudResultsContainer, and UserSettings]
  * showChangedNikudMessage() [for ManualNikudPane and NikudOptionsPane]
  * callForNewNikud() [for ManualNikudPane and EditNikudPane]
  * getSafeWordAndLetter() [for EditNikudPane]
It has the "common dependency" methods:
  * getSafeWordAndLetter() [exposed (as above), AND used by the vuexUndoRedo internal method updateContext(),
                            in turn used by its own methods redo() and undo() (both used by NikudResultsContainer)]
  * getAllAlikeWordsToChange [by wordMutatingMixins method changeAllAlikeWordsToNewNikud (NikudOptionsPane & ManualNikudPane)]
(all else should be considered "internal")
*************************************** */

import $ from 'jquery'
import {Actions} from '@/store/stateChanges'
import styleGlobals from 'shared/js/forTextPanel/styleGlobals'
import {stringWithoutMeteggedLetters} from 'shared/js/commonHebrew'
import {getChosenWordString} from '@/js/letterOperations'
import conditionalOptions from 'shared/js/conditionalOptions'
import {BlankDictionary} from '../store/stateParts'

export default {
  name: 'nikudMixins',
  computed: {
    localLetterAddingMode () {
      return this.modesObj.letterAddingMode
    },
    localCurrentWordIndex: function () {
      return this.$store.state.currentPositionData.wordIndex
    },
    localCurrentLetterIndex: function () {
      return this.$store.state.currentPositionData.letterIndex
    },
    localCurrentWord: function () {
      return this.$store.state.allWorkingNikud.wordsArray[this.localCurrentWordIndex]
    },
    localUserDictionary: function () {
      return this.$store.state.account && this.$store.state.account.sync && this.$store.state.account.sync.dictionary
        ? this.$store.state.account.sync.dictionary : BlankDictionary
    },
    currentLetterNotSep: function () {
      return this.localCurrentWord && (this.localCurrentLetterIndex >= this.localCurrentWord.sepBefore.length) &&
        (this.localCurrentLetterIndex < (this.localCurrentWord.sepBefore.length + this.localCurrentWord.standardLetterArray.length))
    }
  },
  methods: {
    modalOpen () {
      return document.getElementsByClassName('modal show').length > 0
        || document.getElementsByClassName('login-modal').length > 0
    },
    display (text, aimCondition = false, lineBreakCondition = false) {
      return text.replace(new RegExp(styleGlobals.SPACE_SUBSTITUTE, 'g'), ' ').replace(/\n/g, lineBreakCondition ? '↳' : '\n').replace(/(.)ֽ/g, aimCondition ? '<span style="color: ' + styleGlobals.hideableColor + '">' + '$1' + '</span>' : '')
    }, // note: above functionality is repeated in NikudResultsContainer.getWordHtml (it HAS to do 1 span per letter)
    getAllAlikeWordsToChange () {
      const nikudToApply = this.localCurrentWord.chosenWordString
      const lettersToMatch = stringWithoutMeteggedLetters(nikudToApply)
      const conditionArraysToMatch = this.conditionalDictStringsMatchingCurrentWord.map(dictString =>
        conditionalOptions.optionObjFromDictString(dictString).conditions)
      var wordsToChange = [] // NOT counting the "current" one which was changed "just now"
      const wordsArray = this.$store.state.allWorkingNikud.wordsArray
      wordsArray.forEach((wordObj, wordIndex) => {
        if ((this.applyToAllMode === 'all')
            || ((this.applyToAllMode === 'below') && (wordIndex > this.localCurrentWordIndex))
            || ((this.applyToAllMode === 'unedited') && !(wordObj.altered))) {
          const nikudOfWordInDoc = wordObj.chosenWordString
          var lettersOfWordInDoc = stringWithoutMeteggedLetters(nikudOfWordInDoc)
          if (lettersOfWordInDoc === lettersToMatch
              && nikudOfWordInDoc !== nikudToApply
              && (!(conditionArraysToMatch.length) || conditionArraysToMatch.some(conditionsArray =>
                conditionalOptions.testConditions(conditionsArray, wordsArray, wordIndex)))) {
            var newWordContainer = {
              index: wordIndex, changes: { 
                altered: true,
              }
            }
            var optionIndex = wordObj.options.map(key => key.displayText).indexOf(nikudToApply)
            if (optionIndex !== -1) {
              newWordContainer.changes["chosenOptionIndex"] = optionIndex
              newWordContainer.changes["choiceType"] = 'option'
            } else {
              newWordContainer.changes["manualNikud"] = nikudToApply
              newWordContainer.changes["choiceType"] = 'manual'
            }
            wordsToChange.push({
              contextData: { wordIndex: this.localCurrentWordIndex, letterIndex: this.localCurrentLetterIndex }, // for undo
              newWordContainer: newWordContainer
            })
          }
        }
      })
      return wordsToChange
    },
    callForNewNikud (newText) {
      // console.log('newText:')
      // console.log(newText)
      this.$store.commit('SET_TEXT_FOR_NAKDAN_API_CALL', newText)
      this.$store.dispatch(Actions.RUN_NAKDAN)
    },
    getSafeWordAndLetter (wordIndex, letterIndex) {
      const wArray = this.$store.state.allWorkingNikud.wordsArray
      var word, letter
      if (wArray.length) {
        word = Math.min(wordIndex, wArray.length - 1)
        const wordLength = wArray[word].sepBefore.length 
          + wArray[word].standardLetterArray.length + wArray[word].sepAfter.length
        letter = letterIndex === -1 ? wArray[word].sepBefore.length : Math.min(letterIndex, wordLength - 1)
      } else {
        word = -1
        letter = -1
      }
      return { word: word, letter: letter }
    },
    showChangedNikudMessage (changed) {
      $("#changes-applied-msg p").text('השינוי הוחל בעוד ' + changed + ' מקומות')
      $("#changes-applied-msg").fadeIn()
      setTimeout(() => { $("#changes-applied-msg").fadeOut() }, 2000)
    },
    showPasswordMessage (message) {
      $("#change-password-msg p").html(message)
      $("#change-password-msg").fadeIn()
      setTimeout(() => { 
        $("#change-password-msg").fadeOut() 
      }, 4000)
    },
    showAddedToUserDictionaryMessage (type = 'positive') {
      $("#changes-applied-msg p").text(type === 'positive' ? 'האפשרות נוספה למאגר' 
                                        : (type === 'negative' ? 'המילה סומנה כמילה פגומה'
                                            : (type === 'pinned' ? 'המילה הוצמדה לראש הרשימה'
                                                : 'הצמדת המילה לראש בוטלה')))
      $("#changes-applied-msg").fadeIn()
      setTimeout(() => { $("#changes-applied-msg").fadeOut() }, 2000)
    },
    removeFromUserDictionary (entry) {
      const dictString = typeof entry === 'string' ? entry 
                                                   : conditionalOptions.dictStringFromOptionObj(entry)
      this.$store.commit('UPDATE_USER_DICTIONARY', {
        contextData: { wordIndex: this.localCurrentWordIndex, letterIndex: this.localCurrentLetterIndex }, // for undo
        wordsToDelete: [dictString]
      })
    },
    removeFromUserDictionaryNegatives (entry) {
      const dictString = typeof entry === 'string' ? entry 
                                                   : conditionalOptions.dictStringFromOptionObj(entry)
      this.$store.commit('UPDATE_USER_DICTIONARY', {
        contextData: { wordIndex: this.localCurrentWordIndex, letterIndex: this.localCurrentLetterIndex }, // for undo
        negativeWordsToDelete: [dictString]
      })
    },
    getRelevantOptionsFromUserDictionary (wordObj) {
      const query = this.localUserDictionary.lettersMap
        && this.localUserDictionary.lettersMap[stringWithoutMeteggedLetters(getChosenWordString(wordObj))]
      return query ? query : []
    },
    getRelevantNegativeOptionsFromUserDictionary (wordObj) {
      const query = this.localUserDictionary.negativeLettersMap
        && this.localUserDictionary.negativeLettersMap[stringWithoutMeteggedLetters(getChosenWordString(wordObj))]
      return query ? query : []
    }
  }
}
